import React, { InputHTMLAttributes, useCallback, useEffect, useRef } from 'react';

import styled from 'styled-components';

import Checkbox from './ui/Checkbox';
import useModal from 'hooks/useModal';
import Button from './ui/Button';

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9991;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
`;

const Container = styled.div<{ $maxWidth?: number }>`
  padding: 24px;
  position: relative;
  background-color: ${(props) => props.theme.global.bgColor};
  border-radius: 4px;
  max-width: ${({ $maxWidth }) => `${$maxWidth}px`};
  width: ${({ $maxWidth }) => ($maxWidth ? '100%' : 'initial')};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.div`
  font-weight: 500;
  font-size: 18px;
  text-align: center;
`;

const Content = styled.div`
  padding: 20px 0;
  padding-bottom: 12px;
  white-space: pre-wrap;
  text-align: center;
`;

const ButtonView = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 12px;

  button {
    width: 94px;
  }
`;

const CheckBoxRow = styled.div`
  margin-bottom: 12px;
`;

interface DialogBtnGroupProps {
  onCancel?: () => void;
  onConfirm?: () => void;
  cancelText?: string;
  confirmText: string;
}

export const DialogBtnGroup = ({ cancelText, confirmText, onCancel, onConfirm }: DialogBtnGroupProps) => {
  const modal = useModal();
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  const onCancelClick = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    modal.close();
  }, [modal, onCancel]);

  const onConfirmClick = useCallback(() => {
    if (onConfirm) {
      onConfirm();
    }
    modal.close();
  }, [modal, onConfirm]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const { key } = event;

      if (key === 'Enter') {
        return onConfirmClick();
      }
      if (key === 'Escape') {
        return onCancelClick();
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => window.removeEventListener('keydown', handleKeydown);
  }, [onCancelClick, onConfirmClick]);

  useEffect(() => {
    if (confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, [confirmBtnRef]);

  return (
    <ButtonView>
      {cancelText && (
        <Button onClick={onCancelClick} $isReverse>
          {cancelText}
        </Button>
      )}
      <Button ref={confirmBtnRef} onClick={onConfirmClick}>
        {confirmText}
      </Button>
    </ButtonView>
  );
};

export const DialogCheckBox = React.forwardRef(
  (props: InputHTMLAttributes<HTMLInputElement>, ref: React.ForwardedRef<HTMLInputElement>) => {
    return (
      <CheckBoxRow>
        <Checkbox
          ref={ref}
          id="dialog_checkbox"
          label="다시 알림창을 띄우지 않을래요"
          size={20}
          fontSize={14}
          checked={props.checked}
          onChange={props.onChange}
        />
      </CheckBoxRow>
    );
  }
);

export const DialogContent = ({ children }: { children: React.ReactNode }) => {
  return <Content>{children}</Content>;
};

export const DialogTitle = ({ children }: { children: React.ReactNode }) => {
  return <Title>{children}</Title>;
};

interface DialogContainerProps {
  maxWidth?: number;
  children: React.ReactNode;
}

export const DialogContainer = ({ maxWidth, children }: DialogContainerProps) => {
  return <Container $maxWidth={maxWidth}>{children}</Container>;
};

const Dialog = ({ children }: { children: React.ReactNode }) => {
  return <Background>{children}</Background>;
};

export default Dialog;
